import type { TestSession } from '../data/projectTypes'

// Sessions store no kind. A version round is scoped by its version; the version-less lineage holds one
// open session that absorbs whatever overlaps (see ensureActiveTestSession), so its kind follows what it
// contains.
export type TestSessionKind =
  | { type: 'monitoring' }
  | { type: 'version'; trigger: TestSessionTrigger; retest: boolean }
  | { type: 'project'; explored: boolean }

/** What a version round tests: a pull request's head, or a commit deployed from a branch. `sha`: a commit
 *  hash only — version shas are not always one (e.g. a deployment monitor without one). */
export type TestSessionTrigger =
  | { type: 'pull-request'; number: number; repo: string | null }
  | { type: 'deployment'; branch: string | null; sha: string | null; repo: string | null }

const COMMIT_SHA = /^[0-9a-f]{7,64}$/i

export function pullRequestTrigger(number: number, repo: string | null): TestSessionTrigger {
  return { type: 'pull-request', number, repo }
}

/** `projectRepo`: the project's GitHub repository, which deployments are built from. */
export function testSessionKind(
  session: Pick<TestSession, 'monitoring_day' | 'version_id' | 'pr_number' | 'pr_repo_full_name' | 'branch_name' | 'git_sha' | 'ordinal' | 'explorations'>,
  projectRepo: string | null,
): TestSessionKind {
  if (session.monitoring_day) return { type: 'monitoring' }
  if (session.version_id) {
    const trigger: TestSessionTrigger = session.pr_number == null
      ? { type: 'deployment', branch: session.branch_name ?? null, sha: session.git_sha && COMMIT_SHA.test(session.git_sha) ? session.git_sha : null, repo: projectRepo }
      : pullRequestTrigger(session.pr_number, session.pr_repo_full_name ?? null)
    return { type: 'version', trigger, retest: session.ordinal > 1 }
  }
  return { type: 'project', explored: session.explorations.length > 0 }
}

export type TestSessionExplorationSource =
  | { type: 'records'; explorationIds: string[] }
  | { type: 'browse'; taskId: string }

/** Whether sessions of this kind explore by design. A re-test only re-runs the version's journeys. */
export function testSessionKindExplores(kind: TestSessionKind): boolean {
  switch (kind.type) {
    case 'monitoring':
      return false
    case 'project':
      return kind.explored
    case 'version':
      return !kind.retest
  }
}

/** The recording of the session's exploration: its exploration rows, else its version round's browse task. */
export function testSessionExplorationSource(
  session: Pick<TestSession, 'explorations' | 'version_task_id'>,
): TestSessionExplorationSource | null {
  if (session.explorations.length > 0) {
    return { type: 'records', explorationIds: session.explorations.map(exploration => exploration.id) }
  }
  return session.version_task_id ? { type: 'browse', taskId: session.version_task_id } : null
}
